import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const QuestionFormPage = () => {
  const [attachment, setAttachment] = useState(null);
  const [meta, setMeta] = useState({ section: 'A', year: '', courseName: '' });
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQuestionsFromNav = location.state?.parsedQuestions || [];

  const handleParse = async () => {
    if (!attachment) return alert('No file selected');
    const formData = new FormData();
    formData.append('attachment', attachment);
    try {
      const res = await api.post('/questions/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const target = '/teachers/dashboard/questions/new';
      console.log('Navigating to', target);
      navigate(target, { state: { parsedQuestions: res.data } });
    } catch (err) {
      console.error(err);
      alert('Parse failed');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setProgress(0);
    try {
      // Use bulk upload for better performance
      const questionsData = parsedQuestionsFromNav.map((question) => ({
        text: question.text,
        options: question.options,
        answer: question.answer,
        topic: question.topic,
        timeLimit: question.timeLimit,
        images: question.images || [] // Include images array
      }));

      const metadata = {
        section: meta.section,
        year: parseInt(meta.year, 10),
        subject: meta.courseName
      };

      const response = await api.post('/questions/bulk', {
        questions: questionsData,
        metadata: metadata
      });

      console.log(`✅ Bulk saved ${response.data.created} questions to bank`);
      navigate('/teachers/dashboard/quiz/upload');
    } catch (err) {
      // Fallback to individual saves if bulk fails
      console.warn('Bulk save failed, falling back to individual saves:', err);
      
      for (let i = 0; i < parsedQuestionsFromNav.length; i++) {
        await api.post('/questions', {
          text: parsedQuestionsFromNav[i].text,
          options: JSON.stringify(parsedQuestionsFromNav[i].options),
          answer: parsedQuestionsFromNav[i].answer,
          section: meta.section,
          year: parseInt(meta.year, 10),
          subject: meta.courseName,
          topic: parsedQuestionsFromNav[i].topic,
          timeLimit: parsedQuestionsFromNav[i].timeLimit,
          originalOrder: i + 1, // Save the original order (1-based indexing)
          images: parsedQuestionsFromNav[i].images || [] // Include images array
        });
        setProgress(i + 1);
      }
      navigate('/teachers/dashboard/quiz/upload');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-lg mx-auto">
      {parsedQuestionsFromNav.length > 0 ? (
        <div>
          <button
            onClick={handleSave}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded mb-6 w-full font-bold"
            disabled={saving}
          >
            {saving ? `Saving... (${progress}/${parsedQuestionsFromNav.length})` : 'Save All to Bank'}
          </button>
          {saving && (
            <div className="flex flex-col items-center justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-2"></div>
              <span className="text-green-700 font-semibold">Saving {progress}/{parsedQuestionsFromNav.length}...</span>
            </div>
          )}
          <h2 className="text-2xl mb-4">Review Parsed Questions</h2>
          <div className="mb-4">
            <h3 className="text-lg mb-2">Question Group Details</h3>
            <div className="flex space-x-4 mb-4">
              <div>
                <label htmlFor="section" className="block">Section</label>
                <select id="section" value={meta.section} onChange={e => setMeta({ ...meta, section: e.target.value })} className="border p-2">
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select>
              </div>
              <div>
                <label htmlFor="year" className="block">Year</label>
                <input id="year" type="number" value={meta.year} onChange={e => setMeta({ ...meta, year: e.target.value })} className="border p-2" />
              </div>
              <div className="flex-1">
                <label htmlFor="courseName" className="block">Course Name</label>
                <input id="courseName" type="text" value={meta.courseName} onChange={e => setMeta({ ...meta, courseName: e.target.value })} className="w-full border p-2" />
              </div>
            </div>
          </div>
          {parsedQuestionsFromNav.map((q, i) => (
            <div 
              key={i} 
              className={`border p-4 mb-4 rounded-lg ${
                q.parseError 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-200'
              }`}
            >
              {q.parseError && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-3 rounded text-sm">
                  <div className="flex items-start">
                    <span className="text-xl mr-2">⚠️</span>
                    <div>
                      <p className="font-bold">Parsing Error - Question {i + 1}</p>
                      <p className="text-xs mt-1">This question failed to parse correctly. Please review before saving.</p>
                      {q.errorMessage && (
                        <p className="text-xs mt-1 font-mono bg-red-200 p-1 rounded">{q.errorMessage}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className={`mb-2 font-semibold ${q.parseError ? 'text-red-800' : 'text-gray-800'}`}>
                Question {i + 1} {q.parseError && <span className="text-red-600 text-sm">(Needs Review)</span>}
              </div>
              
              {/* Display question images if they exist */}
              {(() => {
                // Parse images if it's a string, otherwise use as-is
                let images = q.images;
                if (typeof images === 'string') {
                  try {
                    images = JSON.parse(images);
                  } catch (e) {
                    images = [];
                  }
                }
                // Ensure it's an array
                if (!Array.isArray(images)) {
                  images = [];
                }
                
                return images.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {images.map((imgSrc, imgIdx) => (
                      <img 
                        key={imgIdx}
                        src={imgSrc} 
                        alt={`Question ${i + 1} attachment ${imgIdx + 1}`}
                        className="max-w-xs max-h-48 object-contain border rounded shadow-sm"
                      />
                    ))}
                  </div>
                );
              })()}
              
              <p className="mb-2 whitespace-pre-wrap">{q.text}</p>
              {q.options && q.options.map((opt, idx) => (
                <div key={idx} className="flex items-center mb-1">
                  <input type="radio" disabled checked={opt === q.answer} className="mr-2" />
                  <span>{opt}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor="attachment" className="block">Attachment (PDF/DOCX)</label>
            <input id="attachment" type="file" accept=".pdf,.doc,.docx" onChange={e => setAttachment(e.target.files[0])} className="w-full border p-2" />
          </div>
          <button type="button" onClick={handleParse} className="bg-yellow-500 text-white px-4 py-2 rounded">Parse Attachment</button>
        </div>
      )}
    </div>
  );
};

export default QuestionFormPage;
